import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LogOut,
  User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatProfilePictureUrl } from '../../utils/imageUtils';
import { navigateToMainDomain } from '../../utils/tenantUtils';
import { useTranslation } from 'react-i18next';

export default function UserProfileDropdown({ onClose }: { onClose: () => void }) {
  const { user, logout } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    onClose();
    // Redirect to root domain without subdomain
    navigateToMainDomain('/');
  };


  const handleProfile = () => {
    navigate('/profile');
    onClose();
  };

  return (
    <div className={`absolute right-0 top-12 w-64 rounded-lg shadow-lg py-2 z-50 border ${theme === 'dark'
      ? 'bg-gray-800 border-gray-700'
      : 'bg-white border-gray-200'
      }`}>
      {/* Profile Section */}
      <div className={`px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
        }`}>
        <div className="flex items-center">
          <div className="relative group h-10 w-10 rounded-full overflow-hidden">
            {user?.pfp_url ? (
              <img
                src={formatProfilePictureUrl(user.pfp_url)}
                alt={t('common.profile')}
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  const imgElement = e.target as HTMLImageElement;
                  imgElement.style.display = 'none';
                  const nextElement = imgElement.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className={`${user?.pfp_url ? 'hidden' : 'flex'} h-full w-full bg-blue-500 items-center justify-center`}
            >
              <span className="text-white font-medium">
                {user?.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>{user?.full_name}</p>
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>{user?.email}</p>
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              } capitalize`}>{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-1 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}">
        <button
          onClick={handleProfile}
          className={`w-full px-4 py-2 text-sm text-left flex items-center space-x-2 ${theme === 'dark'
            ? 'text-gray-300 hover:bg-gray-700'
            : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
          <User className="h-4 w-4" />
          <span>{t('navigation.profile', 'Profile')}</span>
        </button>
        <button
          onClick={handleLogout}
          className={`w-full px-4 py-2 text-sm text-left flex items-center space-x-2 ${theme === 'dark'
            ? 'text-gray-300 hover:bg-gray-700'
            : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
          <LogOut className="h-4 w-4" />
          <span>{t('common.logout', 'Sign out')}</span>
        </button>
      </div>
    </div>
  );
}